import { toaster } from '@/components/ui/toaster';
import { conversationQueryKeys } from '@/hooks/queries/conversation-query-keys';
import api from '@/libs/axiosInstance';
import type {
  ApiErrorResponse,
  BulkEmailSendRequest,
  BulkEmailSendResponse,
} from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

export function useSendBulkEmail() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: BulkEmailSendRequest): Promise<BulkEmailSendResponse> => {
      const response = await api.post('/dashboard/affiliates/bulk-email', data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sentBatches'] });
      queryClient.invalidateQueries({ queryKey: conversationQueryKeys.root });
    },
    onError: (error: AxiosError<ApiErrorResponse>) => {
      const errorCode = error.response?.data?.code;
      const fallback = error.response?.data?.message ?? t('bulkEmail.send.error.generic');

      // Prefer per-code translation under errors.<CODE>; fall back to API message.
      const translated = errorCode ? t(`errors.${errorCode}`, { defaultValue: '' }) : '';

      toaster.create({
        title: t('bulkEmail.send.error.title'),
        description: translated || fallback,
        type: 'error',
        duration: 6000,
      });
    },
  });
}
